import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import {
    MDB_TYPES,
    PROTOCOL_RELATIONSHIPS as PROT_RELS,
    SELECTION_TYPES
} from "@meddbriefer/scenario-data/constants";
import { useScenarioEditor } from "./ScenarioEditorContext"

import ProblemsLayout from "./ProblemsLayout";
import {ModelAdmin, InlineModelAdmin, AssociateModel} from "../admins"
import {Text, TextArea, TextInput, Checkbox, DropDown} from "../widgets"


import { genActionRec }  from "../ScenarioTypeEditor/InterventionsEditor/types";
import { getProblemsBC, getProblemBC, getSolutionBC} from "../ScenarioTypeEditor/urls"
import { swapUp, swapDown } from "../ScenarioTypeEditor/utils";

const PROT_REL_OPTS = [PROT_RELS.REQ, PROT_RELS.ALT, PROT_RELS.OPT, PROT_RELS.NOTIND ].map(e => ({id: e, label: e}))

const PhaseDropDown = ({entity, name, changeHandler}) => {
    const {scenario, scenarioType, actions} = useScenarioEditor()
    const [options, setOptions] = useState(null)

    useEffect(
        () => {
            if (!options) {
                const opts = scenarioType._checklist.filter(e => e.type === MDB_TYPES.PHASE)
                if (! (opts.map(e => e.id).includes(entity.phase))) {
                    // invalid phaseID stored in db, set both phase and subPhase values to ""
                    const rec = {...scenario.actions.find(e => e.solutionID === entity.solutionID), phase: "", subPhase: ""}
                    actions.update(rec)
                }
                setOptions(opts)
            }
        }, [entity, scenario.actions, scenarioType._checklist, actions, options]
    )
    if (!options) {
        return (<span></span>)
    }
    return (<DropDown entity={entity} name={name} options={options} changeHandler={changeHandler} />)
}

const SubPhaseDropDown = ({entity, name, changeHandler}) => {
    const {scenario, scenarioType, actions} = useScenarioEditor()
    const [options, setOptions] = useState(null)
    const [phaseUpdated, setPhaseUpdated] = useState(false)
    useEffect(
        () => {
            setPhaseUpdated(true)
        }, [entity.phase]
    )

    useEffect(
        () => {
            if (phaseUpdated) {
                if ("" === entity.phase) {
                    setOptions(null)
                } else {
                    const opts = scenarioType._checklist.filter(e => e.type === MDB_TYPES.SUB_PHASE && e.phase === entity.phase)
                    setOptions(opts)
                    if (!(opts.map(e => e.id).includes(entity.subPhase))) {
                        // bad subPhase id, set to ""
                        const rec = {
                            ...scenario.actions.find(e => e.solutionID === entity.solutionID && e.id === entity.id),
                            subPhase: ""
                        }
                        actions.update(rec)
                    }
                }
                setPhaseUpdated(false)
            }

        }, [phaseUpdated, entity, scenarioType._checklist, scenario.actions, actions]
    )
    if (!options) {
        return (<span></span>)
    }
    return (
        <DropDown
            entity={entity}
            name={name}
            options={options}
            changeHandler={changeHandler}
        />
    )
}

const Select = ({id, multiple = false, options, getValue, setValue}) => {

    const handleChange = (event) => {
        const {id, value, options} = event.target
        let newValue
        if (!multiple) {
            newValue = value
        } else {
            newValue = Array.from(options).filter(opt => !!opt.selected ).map(opt => opt.value)
        }
        console.log(newValue)
        setValue(id, newValue)
    }

    return (
        <select
            id={id}
            multiple={multiple}
            value={getValue(id)}
            onChange={handleChange}
        >
            <option value="" disabled></option>
            {options.map(opt =>
                <option
                    key={opt.id}
                    value={opt.id}
                >
                    {opt.abbrLabel}
                </option>
            )}
        </select>
    )
}

const Prompt = ({prompt, getValue, setValue}) => {

    return (
        <div>
            <i>{prompt.label}</i>
            <br/>
            <Select
                id={prompt.id}
                multiple={prompt.selectionType === SELECTION_TYPES.OOM}
                options={prompt.answers}
                getValue={getValue}
                setValue={setValue}
            />
        </div>

    )
}

const InterventionVariant = ({entity, name, changeHandler}) => {
    const {scenarioType} = useScenarioEditor()
    const [promptsAndOptions, setPromptsAndOptions] = useState(null)
    const [state, setState] = useState(null)

    const getPromptValue = (promptID) => {
        return state[promptID]
    }

    const setPromptValue = (promptID, value) => {
        const newState = {
            ...state,
            [promptID]: value
        }
        setState(newState)
        changeHandler(entity.id, name, newState)
    }

    useEffect(
        () => {
            if (!promptsAndOptions) {
                const tmp = scenarioType.getIntervention(entity.interventionID, true).distPrompts
                const tmp2 = entity[name]
                if (!tmp2) {
                    const tmp3 = {}
                    tmp.forEach(p => {
                        tmp3[p.id] = ""
                    })
                    setState(tmp3)
                    changeHandler(entity.id, name, tmp3)
                } else {
                    setState(tmp2)
                }
                setPromptsAndOptions(tmp)
            }
        }, [entity, name, scenarioType, promptsAndOptions, changeHandler]
    )

    if (!promptsAndOptions || promptsAndOptions.length === 0) {
        return (<span></span>)
    }
    return (
        <>
        {promptsAndOptions.map(prompt =>
            <Prompt
                key={prompt.id}
                prompt={prompt}
                getValue={getPromptValue}
                setValue={setPromptValue}
            />
        )}
        </>
    )
}


const Solution2 = () => {
    const params = useParams()
    const {probID, solID} = params
    const {scenario, scenarioType, solutions, actions} = useScenarioEditor()
    const [prob, setProb] = useState(null)
    const [solution, setSolution] = useState(null)
    const [solutionUpated, setSolutionUpdated] = useState(false)
    const [myActions, setMyActions] = useState(null)
    const [actionsUpdated, setActionsUpdated] = useState(false)

    const updateSolutionFld = (id, fldName, fldVal) => {
        setSolution(prevState => ({...prevState, [fldName]: fldVal}))
        setSolutionUpdated(true)
    }


    const addAction = (_unused1, _unused2, intvID) => {
        const rec = genActionRec(intvID, solID, probID)
        actions.add(rec)
        updateSolutionFld(solID, "actions", solution.actions.concat(rec.id))
        setActionsUpdated(true)
    }

    const updateAction = (actID, fldName, fldVal) => {
        if (fldName === "phase") {
            updateAction(actID, "subPhase", "")
        }
        const rec = {...scenario.actions.find(e => e.id === actID), [fldName]: fldVal}
        actions.update(rec)
        setActionsUpdated(true)
    }

    const removeAction = (item) => {
        // this will be a no-op if the ID doesn't exist (in scenario-type)
        actions.remove(item.id)
        // regardless, we still need to remove item.id from our sorted list of action IDs
        updateSolutionFld(solID, "actions", solution.actions.filter(id => id !== item.id))
        setActionsUpdated(true)
    }

    const moveActionUp = (actionIdx) => {
        const after = swapUp(solution.actions, actionIdx)
        updateSolutionFld(solID, "actions", after)
        setActionsUpdated(true)
    }
    const moveActionDown = (actionIdx) => {
        const after = swapDown(solution.actions, actionIdx)
        updateSolutionFld(solID, "actions", after)
        setActionsUpdated(true)
    }

    const intvLabelGetter = (rec, fld) => {
        return scenarioType.getIntervention(rec.interventionID).label
    }


    useEffect(
        () => {
            const tmp = scenario.problems.find(e => e.id === probID)
            setProb(tmp)
            const found = scenario.solutions.find(e => e.id === solID)
            if (found) {
                setSolution({...found})
                setActionsUpdated(true)
            } else {
                console.log(`solution ${solID} not found`)
            }
        }, [probID, solID, scenario.problems, scenario.solutions]
    )

    useEffect(
        () => {
            if (solutionUpated) {
                solutions.update(solution)
                setSolutionUpdated(false)
            }
        }, [solutionUpated, solution, solutions]
    )

    useEffect(
        () => {
            if (actionsUpdated) {
                setActionsUpdated(false)
                // get data for this solution's actions
                const matches = scenario.actions.filter(e => solution.actions.includes(e.id))
                // sort the data in the way we want it
                const tmp = []
                const deletedIDs = []
                solution.actions.forEach(actID => {
                    const found = matches.find(e => e.id === actID)
                    if (found) {
                        tmp.push(found)
                    } else {
                        // cleanup kruft in db so I don't have to manually clean up bad deletions from the past
                        deletedIDs.push(actID)
                    }
                })
                if (deletedIDs.length > 0) {
                    console.log(`removing non-existant actionID(s) (${deletedIDs.join(', ')}) from solution.actions array`)

                    updateSolutionFld(solution.id, "actions", solution.actions.filter(id => !deletedIDs.includes(id)))
                    setActionsUpdated(true)
                }
                setMyActions(tmp)
            }
        }, [actionsUpdated, solution, scenario]
    )

    if (!myActions) {
        return (<div>loading...</div>)
    }
    return (
        <ProblemsLayout
            breadcrumbs={[
                getProblemsBC(params),
                getProblemBC(params, prob.label),
                getSolutionBC(params, solution.label)
            ]}
        >
            <ModelAdmin
                heading="Solution"
                record={solution}
                fields={[
                    {name: "id", label: "ID", widget: Text, textSize: "small"},
                    {name: "label", label: "Label", widget: TextInput, changeHandler: updateSolutionFld},
                    {name: "protocols", label: "Protocols", widget: TextInput, changeHandler: updateSolutionFld},
                    {name: "minimalWhy", label: "Minimal Why", widget: TextArea, changeHandler: updateSolutionFld},
                    {name: "strictActionOrdering", label: "Strict Action Ordering", widget: Checkbox, changeHandler: updateSolutionFld}
                ]}
            />
            <InlineModelAdmin
                heading="Actions"
                records={myActions}
                fields={[{
                        name: "interventionID",
                        label: "Intervention ID",
                        colWidthPct: 6,
                        widget: Text,
                        textSize: "xx-small"
                    }, {
                        name: "label",
                        label: "Intervention",
                        colWidthPct: 17,
                        widget: Text,
                        valueGetter: intvLabelGetter
                    }, {
                        name: "interventionVariant",
                        label: "Intervention Variant",
                        colWidthPct: 23,
                        widget: InterventionVariant,
                        changeHandler: updateAction
                    }, {
                        name: "phase",
                        label: "Phase",
                        colWidthPct: 5,
                        widget: PhaseDropDown,
                        changeHandler: updateAction
                    }, {
                        name: "subPhase",
                        label: "SubPhase",
                        colWidthPct: 7,
                        widget: SubPhaseDropDown,
                        changeHandler: updateAction
                    }, {
                        name: "protocolRelationship",
                        label: "Protocol Relationship",
                        colWidthPct: 12,
                        widget: DropDown,
                        options: PROT_REL_OPTS,
                        changeHandler: updateAction
                    }, {
                        name: "why",
                        label: "Why",
                        colWidthPct: 20,
                        widget: TextArea,
                        cols: 30,
                        rows: 3,
                        changeHandler: updateAction
                    }, {
                        name: "contributesToSolution",
                        label: "Contrib. to solution",
                        colWidthPct: 5,
                        widget: Checkbox,
                        changeHandler: updateAction
                    }
                ]}
                allowChangeOrder={true}
                onMoveUp={moveActionUp}
                onMoveDown={moveActionDown}
                editLink={false}
                onDelete={removeAction}
            >
                <AssociateModel
                    model={{type: MDB_TYPES.INTERV, name: "Intervention"}}
                    choices={scenarioType._interventions}
                    search={true}
                    addHandler={addAction}
                />
            </InlineModelAdmin>
        </ProblemsLayout>
    )
}

export default Solution2